import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { IApiMTradingAccount } from '@api/interfaces/data-contracts';
import LockIcon from '@assets/icons/smallLockIcon.svg';
import { SvgIcon } from '@components-library/Icons/SvgIcon';
import AccountDetailsTabs from '@components-member/TradingAccounts/AccountDetails/AccountDetailsTabs';
import AccountDetailsTabsPanel from '@components-member/TradingAccounts/AccountDetails/AccountDetailsTabsPanel';
import { ENUM_CONSTANTS } from '@components-shared/enum.constants';
import { IEnumConstant } from '@components-shared/enum.constants.interface';
import ErrorView from '@components-shared/errorView';
import { Preloader } from '@components-shared/preloader';
import { KeyMapKeys, keyMap } from '@locales/lokalise/keyMap';
import { getFormattedNumber } from '@utils/helper';
import { DepositWarningMessage, PspInfoMessages } from 'components/user/userDataProvider';
import { IBrokerParams, IOnboardingConfiguration, IUser, IUserProfile } from 'global.interfaces';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';

interface ITabsContentProps {
  userProfile: IUserProfile;
  onboardingConfiguration: IOnboardingConfiguration;
  rootPath: string;
  accountId: string;
  account: IApiMTradingAccount;
  renderData: {
    userInfo: IUser;
    brokerParams: IBrokerParams;
    currency: string;
    balance: number;
    login: number;
    accountName: string | null;
    accountEnabled: boolean;
    processing: boolean;
    errMsg: string;
    pathName: string;
    accountPlatform: string;
  };
  onConfirm: () => Promise<void>;
}

export const TabsContent = observer((props: ITabsContentProps): JSX.Element => {
  const { popupModalStore } = useMobxStore();
  const { rootPath, account, renderData, onboardingConfiguration, accountId, onConfirm } = props;
  const { i18n } = useTranslation();
  const {
    userInfo,
    brokerParams,
    processing,
    pathName,
    accountPlatform,
    currency,
    balance,
    login,
    accountName,
    accountEnabled,
    errMsg,
  } = renderData;

  const idx = ENUM_CONSTANTS.TRADING_PLATFORM_TYPE.findIndex(
    (enumConstant: IEnumConstant) => enumConstant.value === accountPlatform,
  );
  const platformType = idx !== -1 ? i18n.t(ENUM_CONSTANTS.TRADING_PLATFORM_TYPE[idx].name as KeyMapKeys) : '';
  const depositTabLocation = pathName.includes('deposit');
  const withdrawalTabLocation = pathName.includes('withdraw');
  const accountDisplayName = accountName ? `- ${accountName}` : '';
  const accountTitle = `${login} ${accountDisplayName}`;

  useEffect(() => {
    if (withdrawalTabLocation) {
      void popupModalStore.fetchUserBankCardRequiresUpdatesAndShowModal();
    }
  }, [popupModalStore, withdrawalTabLocation]);

  if (errMsg) {
    return <ErrorView error={errMsg} />;
  }

  if (processing) {
    return <Preloader />;
  }

  return (
    <div className="content">
      <div className="back-container">
        <NavLink to="/member" className="btn btn--back">
          {i18n.t(keyMap.sharedActionsBack)}
        </NavLink>
      </div>
      <div className="content__header">
        <div className="content__header-right">
          <div className="ma-info">
            <span className="ma-info__label">{`${i18n.t(keyMap.commonBalance)}:`}</span>
            <span className="ma-info__value">
              {currency} {getFormattedNumber(balance, { precision: 2 })}
            </span>
          </div>
        </div>

        <div className="content__title">
          <span className="label-box">{platformType}</span>
          <span>{accountTitle}</span>
          {!accountEnabled && (
            <span className="ma-account__lock-icon">
              <SvgIcon src={LockIcon} />
            </span>
          )}
        </div>

        <DepositWarningMessage
          userInfo={userInfo}
          depositTabLocation={depositTabLocation}
          brokerParams={brokerParams}
        />
        <div className="panel">
          <PspInfoMessages
            userInfo={userInfo}
            depositTabLocation={depositTabLocation}
            withdrawalTabLocation={withdrawalTabLocation}
          />

          <div className="tabs horizontal--tabs">
            <AccountDetailsTabs
              account={account}
              rootPath={rootPath}
              onboardingConfiguration={onboardingConfiguration}
            />
            {account && (
              <AccountDetailsTabsPanel
                rootPath={rootPath}
                accountId={accountId}
                account={account}
                userInfo={userInfo}
                onboardingConfiguration={onboardingConfiguration}
                onConfirm={onConfirm}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
